import apiClient from './apiClient';

/**
 * Anhänge des Chats. Eine Datei wird beim Auswählen hochgeladen und gehört von
 * da an zur Sitzung. PDF, Bilder, Audio und Video gehen mit der nächsten Frage
 * als Datei an das Modell, das sie selbst liest; DOCX, TXT und MD gehen als
 * gewonnener Text in den Fallkontext. Danach bleiben sie im Kontext des Chats
 * und gehen mit jeder weiteren Frage erneut mit (AG-6). Der Browser kennt nur
 * die Angaben über die Datei -- ihren Inhalt bekommt er vom Server nie zurück.
 */

export interface ChatAttachmentSummary {
  id: string;
  fileName: string;
  mimeType?: string;
  sizeBytes: number;
  textChars?: number;
  truncated?: boolean;
  /** 'native': die Datei geht an das Modell; 'text': ihr Text geht als Fallkontext. */
  delivery?: 'native' | 'text';
  status?: 'ready' | 'failed';
  errorMessage?: string;
}

/**
 * Ein Dokument im Kontext des Chats: ein Anhang, den eine frühere Frage schon
 * mitgenommen hat. `inContext` sagt, ob er mit der nächsten Frage wieder geht;
 * `available`, ob der Server seine Datei noch hat (alte Chats: nein).
 */
export interface ChatContextDocument {
  id: string;
  fileName: string;
  mimeType?: string;
  sizeBytes: number;
  delivery?: 'native' | 'text';
  inContext: boolean;
  available: boolean;
}

/** Der Hinweis zur Aufbewahrung beim Hochladen. */
export const CHAT_CONTEXT_PRIVACY_NOTE = 'Dokumente bleiben verschlüsselt im Chat gespeichert und werden bei jeder Frage '
  + 'in diesem Chat erneut an die Recherche übermittelt, bis Sie sie aus dem Kontext nehmen oder den Chat löschen.';

/** Audio und Video kosten nach ihrer Dauer, und das mit jeder Frage. */
export const CHAT_CONTEXT_COST_HINT = 'Kostet bei jeder Frage erneut';

export function isTimedMedia(mimeType: string | undefined): boolean {
  return typeof mimeType === 'string' && (mimeType.startsWith('audio/') || mimeType.startsWith('video/'));
}

/**
 * Technische Schranke der offenen Anhänge eines Chats, wie am Server. Eine
 * fachliche Grenze der Anzahl gibt es nicht.
 */
export const CHAT_ATTACHMENT_MAX_OPEN = 500;

/** Größte Datei; mehr nimmt der Hochladehelfer der Recherche nicht an. */
export const CHAT_ATTACHMENT_MAX_BYTES = 50 * 1024 * 1024;

/**
 * Dateien je Auftrag und Bytes je Auftrag. Hochgeladen wird in kleinen
 * Gruppen nacheinander, damit der Server nie Hunderte Megabyte zugleich hält.
 * Eine einzelne große Datei bildet ihre eigene Gruppe.
 */
export const CHAT_ATTACHMENT_BATCH_FILES = 5;
export const CHAT_ATTACHMENT_BATCH_BYTES = 50 * 1024 * 1024;

/** Dateiarten, deren Text der Server liest (DOCX, TXT, MD): Endungen und Arten. */
const CHAT_ATTACHMENT_TEXT_TYPES = [
  '.docx',
  '.txt',
  '.md',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
];

/** Alle Dateiarten, die der Dateiwähler anbietet: Endungen und Arten. */
export const CHAT_ATTACHMENT_ACCEPT = [
  '.pdf',
  '.png', '.jpg', '.jpeg', '.jfif', '.webp', '.heic', '.heif', '.gif', '.bmp', '.ico', '.avif', '.tif', '.tiff',
  '.wav', '.mp3', '.aac', '.ogg', '.flac', '.m4a',
  '.mp4', '.m4v', '.mpeg', '.mpg', '.mov', '.webm', '.avi', '.flv', '.3gp', '.wmv',
  'application/pdf',
  'image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif',
  'image/gif', 'image/bmp', 'image/x-icon', 'image/avif', 'image/tiff',
  'audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/aac', 'audio/ogg', 'audio/flac', 'audio/x-m4a', 'audio/mp4',
  'video/mp4', 'video/mpeg', 'video/quicktime', 'video/webm', 'video/x-msvideo', 'video/x-flv', 'video/3gpp',
  'video/x-ms-wmv',
  ...CHAT_ATTACHMENT_TEXT_TYPES,
].join(',');

/** Nur die Textanhänge; die Cortecs-Modelle der Bahn D lesen keine Dateien. */
export const CHAT_ATTACHMENT_TEXT_ACCEPT = CHAT_ATTACHMENT_TEXT_TYPES.join(',');

/** Die angenommenen Dateiarten in Worten, für Knopf und Hinweise. */
export const CHAT_ATTACHMENT_TYPES_LABEL = 'PDF, Bilder, Audio, Video, DOCX, TXT, MD';
export const CHAT_ATTACHMENT_TEXT_TYPES_LABEL = 'DOCX, TXT, MD';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Ein einzelner Anhang aus einer Antwort des Servers, oder nichts. */
function toAttachmentSummary(value: unknown): ChatAttachmentSummary | null {
  if (!isRecord(value)) return null;
  const { id, fileName, sizeBytes } = value;
  if (typeof id !== 'string' || id.length === 0) return null;
  if (typeof fileName !== 'string' || fileName.length === 0) return null;
  return {
    id,
    fileName,
    sizeBytes: typeof sizeBytes === 'number' && Number.isFinite(sizeBytes) ? sizeBytes : 0,
    ...(typeof value.mimeType === 'string' ? { mimeType: value.mimeType } : {}),
    ...(typeof value.textChars === 'number' ? { textChars: value.textChars } : {}),
    ...(typeof value.truncated === 'boolean' ? { truncated: value.truncated } : {}),
    ...(value.delivery === 'native' || value.delivery === 'text' ? { delivery: value.delivery } : {}),
    ...(value.status === 'failed' ? { status: 'failed' as const } : { status: 'ready' as const }),
    ...(typeof value.errorMessage === 'string' ? { errorMessage: value.errorMessage } : {}),
  };
}

export function normalizeChatAttachments(value: unknown): ChatAttachmentSummary[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const summary = toAttachmentSummary(entry);
    return summary ? [summary] : [];
  });
}

/** Die Dokumente im Kontext aus einer Antwort des Servers. */
export function normalizeChatContext(value: unknown): ChatContextDocument[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const summary = toAttachmentSummary(entry);
    if (!summary || !isRecord(entry)) return [];
    return [{
      id: summary.id,
      fileName: summary.fileName,
      sizeBytes: summary.sizeBytes,
      ...(summary.mimeType ? { mimeType: summary.mimeType } : {}),
      ...(summary.delivery ? { delivery: summary.delivery } : {}),
      inContext: entry.inContext !== false,
      available: entry.available !== false,
    }];
  });
}

/** Die Größe einer Datei, wie sie am Chip steht. */
export function formatAttachmentSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${Math.round(bytes)} Byte`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Nur ein gelesener Anhang trägt Text bei; ein gescheiterter bleibt am Chip
 * sichtbar, geht aber nicht mit der Frage hinaus.
 */
export function readyAttachmentIds(attachments: ReadonlyArray<ChatAttachmentSummary>): string[] {
  return attachments.filter((attachment) => attachment.status !== 'failed').map((attachment) => attachment.id);
}

/**
 * Ob die Auswahl unter die technische Schranke passt. Sie liegt so hoch, dass
 * sie im Alltag nie greift; die Auswahl wird trotzdem vor dem Hochladen geprüft.
 */
export function attachmentSelectionError(open: number, incoming: number): string | null {
  if (incoming === 0) return null;
  if (open + incoming > CHAT_ATTACHMENT_MAX_OPEN) {
    return `Pro Recherche sind höchstens ${CHAT_ATTACHMENT_MAX_OPEN} offene Anhänge möglich.`;
  }
  return null;
}

/**
 * Trennt Dateien über der Größengrenze ab, bevor sie hochgehen: der Server
 * wiese sonst die ganze Gruppe ab, in der sie steht.
 */
export function splitOversizedAttachments<T extends { name: string; size: number }>(
  files: ReadonlyArray<T>,
): { accepted: T[]; error: string | null } {
  const accepted = files.filter(file => file.size <= CHAT_ATTACHMENT_MAX_BYTES);
  const rejected = files.filter(file => file.size > CHAT_ATTACHMENT_MAX_BYTES);
  if (rejected.length === 0) return { accepted, error: null };
  const names = rejected.map(file => `"${file.name}"`).join(', ');
  return {
    accepted,
    error: rejected.length === 1
      ? `Die Datei ${names} ist größer als 50 MB und wurde nicht hochgeladen.`
      : `Die Dateien ${names} sind größer als 50 MB und wurden nicht hochgeladen.`,
  };
}

/**
 * Teilt die Auswahl in Gruppen zu höchstens fünf Dateien und 50 MB; eine
 * Datei, die allein schon so groß ist, geht für sich.
 */
export function chatAttachmentBatches<T extends { size: number }>(files: ReadonlyArray<T>): T[][] {
  const batches: T[][] = [];
  let current: T[] = [];
  let currentBytes = 0;
  for (const file of files) {
    if (current.length > 0
      && (current.length >= CHAT_ATTACHMENT_BATCH_FILES || currentBytes + file.size > CHAT_ATTACHMENT_BATCH_BYTES)) {
      batches.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(file);
    currentBytes += file.size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

/**
 * Der Grund, den der Server genannt hat. Ohne eigene Meldung bleibt ein
 * allgemeiner deutscher Satz -- nie der Wortlaut einer Bibliothek.
 */
export function attachmentRequestError(error: unknown): string {
  const response = (error as { response?: { data?: unknown } })?.response;
  const data = response?.data;
  if (isRecord(data) && typeof data.error === 'string' && data.error.length > 0 && data.error.length <= 500) {
    return data.error;
  }
  return 'Die Datei konnte nicht hochgeladen werden.';
}

/** Die schmale Naht zum Client, damit der Test die Anfrageform prüfen kann. */
export interface ChatAttachmentUploadClient {
  post: typeof apiClient.post;
}

/**
 * Lädt die Dateien gruppenweise nacheinander hoch. Jede fertige Gruppe meldet
 * sich über `onBatch`, damit ihre Chips sofort stehen; scheitert eine spätere
 * Gruppe, bleiben die früheren erhalten und der Fehler geht an den Aufrufer.
 */
export async function uploadChatAttachments(
  sessionId: string,
  files: ReadonlyArray<File>,
  client: ChatAttachmentUploadClient = apiClient,
  onBatch?: (uploaded: ChatAttachmentSummary[], uploadedFiles: number) => void,
): Promise<ChatAttachmentSummary[]> {
  const all: ChatAttachmentSummary[] = [];
  for (const batch of chatAttachmentBatches(files)) {
    const form = new FormData();
    for (const file of batch) form.append('files', file, file.name);
    const response = await client.post(
      `/api/ris/sessions/${encodeURIComponent(sessionId)}/attachments`,
      form,
      // Der gemeinsame Client setzt application/json voraus; ohne diese Kopfzeile schickt der
      // Browser das Formular als JSON und der Server sieht keine Datei (Befund 21.09.2026).
      // Mit multipart/form-data überlässt axios dem Browser die Grenzmarke.
      { timeout: 300_000, headers: { 'Content-Type': 'multipart/form-data' } },
    );
    const uploaded = normalizeChatAttachments(response.data?.attachments);
    all.push(...uploaded);
    onBatch?.(uploaded, batch.length);
  }
  return all;
}

/** Die offenen Anhänge der nächsten Frage und die Dokumente im Kontext des Chats. */
export async function listChatAttachments(
  sessionId: string,
): Promise<{ open: ChatAttachmentSummary[]; context: ChatContextDocument[] }> {
  const response = await apiClient.get(`/api/ris/sessions/${encodeURIComponent(sessionId)}/attachments`);
  return {
    open: normalizeChatAttachments(response.data?.attachments),
    context: normalizeChatContext(response.data?.context),
  };
}

/** Nimmt ein Dokument dauerhaft aus dem Kontext des Chats oder wieder hinein. */
export async function setChatAttachmentInContext(sessionId: string, attachmentId: string, inContext: boolean): Promise<void> {
  await apiClient.patch(
    `/api/ris/sessions/${encodeURIComponent(sessionId)}/attachments/${encodeURIComponent(attachmentId)}/context`,
    { inContext },
  );
}

export async function deleteChatAttachment(sessionId: string, attachmentId: string): Promise<void> {
  await apiClient.delete(
    `/api/ris/sessions/${encodeURIComponent(sessionId)}/attachments/${encodeURIComponent(attachmentId)}`,
  );
}
